/**
 * Gaming Board portal access matrix (product restrictions table)
 *
 * Access levels: no | Read | Full
 * Left menu: show item when access is Read or Full; hide when no.
 *
 * Menu                    | Viewer | Reviewer | Manager | Administrator
 * ------------------------|--------|----------|---------|---------------
 * Dashboard               | no     | Full     | Full    | Full
 * Application Forms       | no     | no       | no      | Full
 * Applications            | Read   | Full     | Full    | Read
 * In Process              | Read   | Full     | Full    | Read
 * Approved                | Read   | Read     | Full    | Read   (status change: Manager)
 * Rejected                | Read   | Read     | Full    | Read   (status change: Manager)
 * Renewal                 | Read   | Full     | Full    | Read
 * Discrepancies           | no     | Full     | Full    | Read
 * Certifications          | Read   | Full     | Full    | Full
 * Registered Employees    | no     | Read     | Read    | Full
 * Registered Companies    | no     | Read     | Read    | Full
 * Users                   | no     | no       | no      | Full
 * Locked Accounts         | no     | no       | no      | Full
 * Feedback                | no     | Full     | Full    | Full
 */

export const ROLES = {
  ADMIN: "admin",
  MANAGER: "manager",
  REVIEWER: "reviewer",
  VIEWER: "viewer",
};

export const GB_PORTAL_ROLES = [
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.REVIEWER,
  ROLES.VIEWER,
];

export const ROLE_OPTIONS = [
  { value: ROLES.ADMIN, label: "Administrator" },
  { value: ROLES.MANAGER, label: "Manager" },
  { value: ROLES.REVIEWER, label: "Reviewer" },
  { value: ROLES.VIEWER, label: "Viewer" },
];

export const ROLES_QUERY = GB_PORTAL_ROLES.join(",");

export const getUserRole = (user) =>
  String(user?.user?.user_role || "").toLowerCase();

export const hasRole = (user, role) => {
  if (!user || !user.user || !role) return false;
  return getUserRole(user) === String(role).toLowerCase();
};

export const hasAnyRole = (user, allowedRoles) => {
  if (!user || !user.user) return false;
  if (!allowedRoles || allowedRoles.length === 0) return false;
  const userRole = getUserRole(user);
  return allowedRoles.some(
    (role) => String(role).toLowerCase() === userRole
  );
};

/** Menu keys → roles that have Read or Full (shown in left nav) */
export const MENU_VISIBLE_ROLES = {
  dashboard: [ROLES.ADMIN, ROLES.MANAGER, ROLES.REVIEWER],
  applicationForms: [ROLES.ADMIN],
  applications: [ROLES.ADMIN, ROLES.MANAGER, ROLES.REVIEWER, ROLES.VIEWER],
  inProcess: [ROLES.ADMIN, ROLES.MANAGER, ROLES.REVIEWER, ROLES.VIEWER],
  approved: [ROLES.ADMIN, ROLES.MANAGER, ROLES.REVIEWER, ROLES.VIEWER],
  rejected: [ROLES.ADMIN, ROLES.MANAGER, ROLES.REVIEWER, ROLES.VIEWER],
  renewal: [ROLES.ADMIN, ROLES.MANAGER, ROLES.REVIEWER, ROLES.VIEWER],
  discrepancies: [ROLES.ADMIN, ROLES.MANAGER, ROLES.REVIEWER],
  certifications: [ROLES.ADMIN, ROLES.MANAGER, ROLES.REVIEWER, ROLES.VIEWER],
  employees: [ROLES.ADMIN, ROLES.MANAGER, ROLES.REVIEWER],
  companies: [ROLES.ADMIN, ROLES.MANAGER, ROLES.REVIEWER],
  users: [ROLES.ADMIN],
  lockedAccounts: [ROLES.ADMIN],
  feedback: [ROLES.ADMIN, ROLES.MANAGER, ROLES.REVIEWER],
};

/** True when the role has Read or Full for that menu (item should appear in left nav) */
export const canSeeMenu = (user, menuKey) => {
  const roles = MENU_VISIBLE_ROLES[menuKey];
  if (!roles) return false;
  return hasAnyRole(user, roles);
};

export const isGamingBoardStaff = (user) =>
  hasAnyRole(user, GB_PORTAL_ROLES) || Boolean(user?.user?.is_admin);

/** Landing path after login / auth redirects */
export const getHomePath = (user) => {
  if (canSeeMenu(user, "dashboard")) {
    return "/dashboard";
  }
  if (canSeeMenu(user, "inProcess")) {
    return "/in-process-applications";
  }
  return "/";
};

// --- Left-menu visibility (Read or Full → show; no → hide) ---
export const canViewDashboard = (user) => canSeeMenu(user, "dashboard");
export const canManageForms = (user) => canSeeMenu(user, "applicationForms");
export const canCreateForms = canManageForms;
export const canAdministerAccess = (user) => canSeeMenu(user, "users");
export const canViewUsers = (user) => canSeeMenu(user, "users");
export const canViewLockedAccounts = (user) => canSeeMenu(user, "lockedAccounts");
export const canViewApplications = (user) => canSeeMenu(user, "applications");
export const canDownloadApplications = canViewApplications;
export const canViewDiscrepancies = (user) => canSeeMenu(user, "discrepancies");
export const canViewCertificates = (user) => canSeeMenu(user, "certifications");
export const canViewEmployees = (user) => canSeeMenu(user, "employees");
export const canViewCompanies = (user) => canSeeMenu(user, "companies");
export const canViewRegistry = (user) =>
  canSeeMenu(user, "employees") || canSeeMenu(user, "companies");
export const canViewFeedback = (user) => canSeeMenu(user, "feedback");

/**
 * Action restrictions removed: any role that can see the menu can use its actions.
 * Menu visibility (canSeeMenu) still controls which pages appear.
 */
export const canMutateInProcess = (user) => canSeeMenu(user, "inProcess");
export const canMutateRenewal = (user) => canSeeMenu(user, "renewal");
export const canReviewApplications = (user) => canSeeMenu(user, "applications");
export const canDecideApplications = (user) =>
  canSeeMenu(user, "approved") || canSeeMenu(user, "rejected");
export const canMutateApproved = (user) => canSeeMenu(user, "approved");
export const canMutateRejected = (user) => canSeeMenu(user, "rejected");
export const canMutateApplications = canReviewApplications;
export const canEditApplications = canReviewApplications;
export const canProcessApplications = canDecideApplications;

export const canMutateDiscrepancies = (user) => canSeeMenu(user, "discrepancies");
export const canRaiseDiscrepancies = canMutateDiscrepancies;
export const canAssignApplications = canMutateDiscrepancies;

/** All statuses available to anyone who can open discrepancies / applications */
export const getAllowedApplicationStates = (user) => {
  if (
    canMutateDiscrepancies(user) ||
    canMutateInProcess(user) ||
    canDecideApplications(user)
  ) {
    return ["in_process", "approved", "rejected", "discrepancy_GB_GB"];
  }
  return [];
};

export const canManageCertificates = (user) => canSeeMenu(user, "certifications");
export const canIssueCertificates = canManageCertificates;

/** Registry amend for anyone who can see employees/companies */
export const canAmendRegistry = (user) =>
  canSeeMenu(user, "employees") || canSeeMenu(user, "companies");

// --- Misc ---
export const canViewFileCabinet = (user) =>
  hasAnyRole(user, [ROLES.ADMIN, ROLES.MANAGER, ROLES.REVIEWER]);
export const canViewNotifications = (user) =>
  hasAnyRole(user, GB_PORTAL_ROLES);
